var BookmarkRepository  = require("Domain/Ott/Api/V2/DataAccess/UserBookmark/Repository"),
    BookmarkService     = require("Domain/Ott/Api/V2/DataAccess/UserBookmark/Service"),
    Component           = require("enyo/Component"),
    DataTransformer     = require("./DataTransformer"),
    EventEmitterSupport = require("enyo-ui/EventEmitterSupport"),
    config              = require("app/config"),
    kind                = require("enyo/kind"),
    logger              = require("enyo/logger");

module.exports = kind(/** @lends  app.Bookmarks.BookmarksService.prototype */ {

    /**
     * @private
     */
    name: "app.Bookmarks.BookmarksService",

    /**
     * @private
     */
    kind: Component,

    /**
     * @private
     */
    mixins: [
        EventEmitterSupport
    ],

    /**
     * @type {Array}
     * @readonly
     * @public
     */
    bookmarks: [],

    /**
     * The language of the current set of bookmarks.
     *
     * @type {string}
     * @readonly
     * @public
     */
    language: null,

    /**
     * The timestamp in seconds since last load.
     *
     * @type {Array}
     * @readonly
     * @public
     */
    lastLoaded: null,

    /**
     * Max age of bookmarks in seconds before the set is considered stale.
     *
     * @type {Number}
     * @public
     */
    maxAge: config["domain/ott/api/v2/users/me/bookmarks"],

    /**
     * @private
     */
    refreshPromise: null,

    /**
     * @private
     */
    loadingLanguage: null,

    /**
     * @readonly
     * @public
     */
    isLoading: false,

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.onLoginStatusChanged = this.bindSafely(this.onLoginStatusChanged);
            this.onLanguageChanged    = this.bindSafely(this.onLanguageChanged);
            this.observe("app.$.authService.isLoggedIn", this.onLoginStatusChanged);
            this.observe("app.$.i18nService.language", this.onLanguageChanged);
        };
    }),

    /**
     * @param  {number}  oldLoginStatus  The old login status
     * @private
     */
    onLoginStatusChanged: function(oldLoginStatus) {
        var loginStatus = this.app.$.authService.get("isLoggedIn"),
            language    = this.app.$.i18nService.get("language");

        this.reset();

        if (loginStatus !== oldLoginStatus && loginStatus === 1 && language) {
            this.refreshBookmarks(language);
        }
    },

    /**
     * @param  {number}  oldLanguage  The old language
     * @private
     */
    onLanguageChanged: function(oldLanguage) {
        var loginStatus = this.app.$.authService.get("isLoggedIn"),
            language    = this.app.$.i18nService.get("language");

        this.reset();

        if (language !== oldLanguage && language && loginStatus === 1) {
            this.refreshBookmarks(language);
        }
    },

    /**
     * Clears the cache and unsets all loading states.
     *
     * @private
     */
    reset: function() {
        this.set("isLoading", false);
        this.set("refreshPromise", null);
        this.set("loadingLanguage", null);
        this.set("lastLoaded", null);
        this.set("bookmarks", []);
    },

    /**
     * @private
     */
    bookmarksChanged: function() {
        this.emit("BookmarkService::updated");
    },

    /**
     * Determines if we need to refresh the bookmarks cache.
     *
     * @param    {string}   language  The desired language.
     * @returns  {boolean}  Returns `true` if refresh is needed, `false`
     *                      otherwise.
     * @public
     */
    needsCacheRefresh: function(language) {
        var currentTimestamp = (new Date()).getTime() / 1000,
            lastLoaded       = this.get("lastLoaded") || 0,
            maxAge           = this.get("maxAge");

        return this.get("language") !== language
            || lastLoaded < currentTimestamp - maxAge;
    },

    /**
     * Returns the cached set of bookmarks if it's fresh enough and of the
     * desired language. If the cached set is stale or of the wrong language,
     * the set will be refreshed.
     *
     * If the `forceRefresh` flag is set to `true`, a fresh set of bookmarks
     * will always be loaded.
     *
     * In all cases, a promise will be returned which, if successful, will
     * resolve with a set of 0 or more bookmarks.
     *
     * @param    {string}   language  The desired language.
     * @param    {boolean}  forceRefresh  Whether to force loading a fresh set
     *                                    of bookmarks from the api.
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    getBookmarks: function(language, forceRefresh) {

        // Load a fresh set of bookmarks if needed
        if (forceRefresh || this.needsCacheRefresh(language)) {
            // Wait for result of current refresh
            if (this.refreshPromise && this.isLoading && this.loadingLanguage === language) {
                return this.refreshPromise;
            }

            // Start a new refresh call
            else {
                return this.refreshBookmarks(language);
            }
        }

        // Otherwise, just resolve with the cached set.
        else {
            return Promise.resolve({
                status: "ok",
                data:   this.get("bookmarks")
            });
        }
    },

    /**
     * Attempt to fetch a fresh set of bookmarks.
     *
     * @param    {string}   language  The desired language.
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    refreshBookmarks: function(language) {
        var that           = this,
            refreshPromise = this.refreshPromise;

        // If no refresh is in progress, trigger one now
        if (!this.isLoading || this.loadingLanguage !== language) {
            refreshPromise = BookmarkRepository.getAll(language);
            this.set("refreshPromise", refreshPromise);
            this.set("loadingLanguage", language);
            this.set("isLoading", true);
            refreshPromise
                .then(function(response) {
                    var bookmarks;

                    // These results are no longer relevant
                    if (that.get("refreshPromise") !== refreshPromise) {
                        return Promise.resolve({
                            status: "canceled"
                        });
                    }

                    if (response.status === "ok") {
                        bookmarks = DataTransformer.transformBookmarks(language, response.data);
                        that.set("lastLoaded", (new Date()).getTime() / 1000);
                        that.set("language", language);
                        that.set("bookmarks", bookmarks);
                        that.set("refreshPromise", null);
                        that.set("isLoading", false);

                        return Promise.resolve({
                            status: "ok",
                            data:   bookmarks
                        });
                    }
                    else {
                        that.set("isLoading", false);

                        return Promise.resolve({
                            status: "error",
                            type:   response.type || "unknown_error"
                        });
                    }
                })

                // Could not load bookmarks
                .catch(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    if (that.get("refreshPromise") === refreshPromise) {
                        that.set("isLoading", false);
                    }

                    return Promise.resolve({
                        status: "error",
                        type:   error.message || "unknown_error"
                    });
                });
        }

        // Return the refresh promise
        return refreshPromise;
    },

    /**
     * Reloads a particular bookmark and injects it back into the cache.
     *
     * @param    {number}   language  The content language
     * @param    {string}   bookmark  The bookmark item
     * @returns  {Promise}  A promise that will resolve with the result.
     * @private
     */
    refreshBookmark: function(language, bookmark) {
        var that = this;

        return BookmarkRepository
            .getByMediaId(language, bookmark.media_id)
            .then(function(response) {
                var bookmark;

                if (response.status === "ok") {
                    bookmark = DataTransformer.transformBookmark(language, response.data);
                    that.addBookmarkToCache(bookmark);

                    return Promise.resolve({
                        status: "ok"
                    });
                }
                else {
                    return Promise.resolve({
                        status: "error",
                        type:   response.type || "unknown_error"
                    });
                }
            })

            // Could not add bookmark
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Try and find a bookmark for the provided media item.
     *
     * @param    {string}   media  The media item.
     * @returns  {?Object}  The bookmark if it exists, null otherwise.
     * @public
     */
    findBookmark: function(media) {
        var bookmarks = this.get("bookmarks") || [],
            index;

        index = bookmarks.findIndex(function(bookmark) {
            return String(bookmark.media_id) === String(media.id);
        });

        return index > -1 ? bookmarks[index] : null;
    },

    /**
     * Adds a new bookmark. If API replies with a success response, the cache
     * gets refreshed.
     *
     * @param    {string}   media  The media item.
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    addBookmark: function(media) {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        return BookmarkService
            .addBookmark(media)
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Media Consumption",
                        "bookmark_media",
                        media.type.replaceAll("_", "-") + "/" + media.meta.slug,
                        null,
                        {
                            language:  media.meta.language,
                            mediaItem: media.type.replaceAll("_", "-") + "/" + media.meta.slug
                        }
                    );

                    return that.refreshBookmark(media.meta.language, response.data);
                }
                else {
                    return Promise.resolve({
                        status: "error",
                        type:   response.type || "unknown_error"
                    });
                }
            })

            // Could not add bookmark
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Clones the current bookmark array and appends the new one to the clone.
     * Then stores the clone in the cache so observers can diff against the
     * previous array if needed.
     *
     * @param  {Object}  newBookmark  The new bookmark.
     * @private
     */
    addBookmarkToCache: function(newBookmark) {
        var bookmarks = this.get("bookmarks"),
            index;

        // Clone the bookmarks cache to make sure change handlers can diff
        // between two separate arrays.
        bookmarks = JSON.parse(JSON.stringify(bookmarks));

        // Look for bookmark in current cache
        index = bookmarks.findIndex(function(bookmark) {
            return String(bookmark.media_id) === String(newBookmark.media_id);
        });

        // Add it if needed
        if (index === -1) {
            bookmarks.unshift(newBookmark);
        }
        else {
            bookmarks[index] = newBookmark;
        }

        this.set("bookmarks", bookmarks);
    },

    /**
     * Adds a new bookmark. If API replies with a success response, the cache
     * gets refreshed.
     *
     * @param    {string}   bookmark  The bookmark item.
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    removeBookmark: function(bookmark) {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        return BookmarkService
            .removeBookmark(bookmark)
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Media Consumption",
                        "unbookmark_media",
                        bookmark.media_item.type.replaceAll("_", "-") + "/" + bookmark.media_item.meta.slug,
                        null,
                        {
                            language:  bookmark.media_item.meta.language,
                            mediaItem: bookmark.media_item.type.replaceAll("_", "-") + "/" + bookmark.media_item.meta.slug
                        }
                    );
                    that.removeBookmarkFromCache(bookmark);

                    return Promise.resolve({
                        status: "ok"
                    });
                }
                else {
                    return Promise.resolve({
                        status: "error",
                        type:   response.type || "unknown_error"
                    });
                }
            })

            // Could not remove bookmark
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Clones the current bookmark array and splices the bookmark out of the
     * clone. Then stores the clone in the cache so observers can diff against
     * the previous array if needed.
     *
     * @param  {Object}  removedBookmark  The removed bookmark.
     * @private
     */
    removeBookmarkFromCache: function(removedBookmark) {
        var bookmarks = this.get("bookmarks"),
            index;

        // Clone the bookmarks cache to make sure change handlers can diff
        // between two separate arrays.
        bookmarks = JSON.parse(JSON.stringify(bookmarks));

        // Look for bookmark in current cache
        index = bookmarks.findIndex(function(bookmark) {
            return String(bookmark.media_id) === String(removedBookmark.media_id);
        });

        // Remove it if needed
        if (index > -1) {
            bookmarks.splice(index, 1);
        }

        this.set("bookmarks", bookmarks);
    }

});
